import { type ReactNode } from "react";

import { BTText } from "@btnet/core-components";

import { BdsIconPencilSimple } from "@buildertrend/components";

import { friendlyDefaultDisplayValue } from "utilities/helpers";

import { BTButton } from "commonComponents/btWrappers/BTButton/BTButton";
import { BTExternalLink } from "commonComponents/btWrappers/BTExternalLink/BTExternalLink";
import {
    LinkFieldEditor,
    normalizeUrl,
} from "commonComponents/entity/customField/utils/customFieldTypes/LinkFieldEditor";
import {
    defaultRenderAgGridCell,
    type ICustomFieldTypeHandler,
} from "commonComponents/entity/customField/utils/customFieldValues.util";

import {
    type CustomFieldAssociatedType,
    type LinkCustomFieldDefinition,
    type LinkCustomFieldValue,
} from "handlers";

export const LinkCustomFieldHandler: ICustomFieldTypeHandler<
    string,
    LinkCustomFieldValue,
    LinkCustomFieldDefinition
> = {
    getValue(field): string | null {
        return field.value || "";
    },

    setValue(field, value): LinkCustomFieldValue {
        return { ...field, value: value ?? "" };
    },

    createValue(
        customFieldId: number,
        associatedType: CustomFieldAssociatedType,
        associatedTypeId: number,
        value: string | null,
    ): LinkCustomFieldValue {
        return {
            dataType: "Link",
            customFieldId,
            customFieldValueId: 0,
            associatedType,
            associatedTypeId,
            value: value ?? "",
        };
    },

    compare(valueA: string | null, valueB: string | null): number {
        const a = valueA ?? "";
        const b = valueB ?? "";
        return a.localeCompare(b, undefined, { sensitivity: "base" });
    },

    renderDisplay(value: string | null): ReactNode {
        if (!value) {
            return <BTText size="normal-md" text={friendlyDefaultDisplayValue} />;
        }

        return (
            <BTExternalLink href={normalizeUrl(value)}>
                <BTText size="normal-md" text={value} />
            </BTExternalLink>
        );
    },

    renderEditor(
        value: string | null,
        customFieldId: number,
        onChange: (newValue: string | null) => void,
        onBlur: () => void,
        autoFocus?: boolean,
    ): ReactNode {
        return (
            <LinkFieldEditor
                value={value}
                customFieldId={customFieldId}
                onChange={onChange}
                onBlur={onBlur}
                startInEditMode={autoFocus}
            />
        );
    },

    getAgGridCellEditor() {
        return "agTextCellEditor";
    },

    renderAgGridCell(context): ReactNode {
        if (!context.value || !context.canEdit) {
            return defaultRenderAgGridCell(context);
        }

        const handleClickCapture = (e: React.MouseEvent) => {
            // Capture click before AG Grid's singleClickEdit intercepts it.
            // If the user clicked the link anchor, let the link navigate (don't enter edit mode).
            const target = e.target as HTMLElement;
            if (target.closest("a")) {
                e.stopPropagation();
            }
        };

        return (
            <div
                className="custom-field-cell-content custom-field-link-cell"
                onClickCapture={handleClickCapture}
            >
                <span className="custom-field-link-text">{context.displayContent}</span>
                <BTButton
                    data-testid="editLinkGridButton"
                    type="tertiary"
                    className="custom-field-link-edit-icon"
                    icon={<BdsIconPencilSimple />}
                    aria-label="Edit link"
                />
            </div>
        );
    },
};
